import React, { FC } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {
  FormLabel,
  Accordion,
  AccordionSummary,
  Theme,
  AccordionDetails,
  Typography,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { AssignmentLate, ExpandMore, Visibility } from '@mui/icons-material';
import { IChecklistReview } from '../../../models';

interface IChecklistReviewMobile {
  original: IChecklistReview;
  handleEdit: (val: IChecklistReview) => void;
  selectedChecklists: IChecklistReview[];
  setSelectedChecklists: React.Dispatch<React.SetStateAction<IChecklistReview[]>>;
  isArchivedChecklists: boolean;
  canProcessPOs: boolean;
}

export const ChecklistReviewMobile: FC<IChecklistReviewMobile> = ({
  original,
  handleEdit,
  selectedChecklists,
  setSelectedChecklists,
  isArchivedChecklists,
  canProcessPOs
}) => {
  const classes = useStyles();
  const { purchaseOrderForeignId, typeCode, vendorName, jobForeignId, taskName, status, poExists } =
    original;
  const ids = selectedChecklists.map(c => c.purchaseOrderChecklistId);
  const isSelected = ids.includes(original.purchaseOrderChecklistId);
  const showCheckbox = original.status === 'Meets Standards';
  const showWarning = original.status !== 'Meets Standards';

  return (
    <Accordion className={classes.root}>
      <AccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="checklist-content"
        id="checklist-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <div className={classes.checkboxAndIdWrap}>
            {!isArchivedChecklists && canProcessPOs && (
              <>
                {showWarning ? (
                  <Tooltip
                    className={classes.duplicateIcon}
                    title={`This checklist is not available for bulk processing due to its status of: ${original.status}`}
                  >
                    <AssignmentLate />
                  </Tooltip>
                ) : (
                  showCheckbox && (
                    <FormControlLabel
                      classes={{
                        root: classes.tableCheckbox
                      }}
                      color="primary"
                      control={
                        <Checkbox
                          checked={isSelected}
                          id="checklists-checkbox-mobile"
                          onClick={e => {
                            e.stopPropagation();
                            isSelected
                              ? setSelectedChecklists(
                                  selectedChecklists.filter(
                                    selectedChecklist =>
                                      selectedChecklist.purchaseOrderChecklistId !==
                                      original.purchaseOrderChecklistId
                                  )
                                )
                              : setSelectedChecklists([...selectedChecklists, original]);
                          }}
                        />
                      }
                      label=""
                    />
                  )
                )}
              </>
            )}
            <Typography variant="body2">{purchaseOrderForeignId}</Typography>
          </div>
          {poExists && (
            <div className={classes.buttonsWrapper}>
              <IconButton
                className={classes.editButton}
                color="primary"
                onClick={e => {
                  e.stopPropagation();
                  handleEdit(original);
                }}
              >
                <Visibility />
              </IconButton>
            </div>
          )}
        </div>
      </AccordionSummary>
      <AccordionDetails className={classes.details}>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Order Number:
          </FormLabel>
          <Typography component="span" variant="body2">
            {purchaseOrderForeignId || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Vendor:
          </FormLabel>
          <Typography component="span" variant="body2">
            {vendorName || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Job:
          </FormLabel>
          <Typography component="span" variant="body2">
            {jobForeignId || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Task:
          </FormLabel>
          <Typography component="span" variant="body2">
            {taskName || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Type:
          </FormLabel>
          <Typography component="span" variant="body2">
            {typeCode || '--'}
          </Typography>
        </div>
        <div className={classes.panelSummaryWrapper}>
          <FormLabel component="span" className={clsx(classes.boldName, classes.subLabel)}>
            Status:
          </FormLabel>
          <Typography component="span" variant="body2">
            {status || '--'}
          </Typography>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    '&:first-child': { marginTop: 0 }
  },
  panelSummaryWrapper: {
    '&:first-child': { marginTop: 0 }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: '12px 16px',
    backgroundColor: theme.palette.grey[100]
  },
  checkboxAndIdWrap: {
    display: 'flex',
    alignItems: 'center'
  },
  editButton: {
    padding: `2px 5px`
  },
  deleteButton: {
    padding: `2px 5px`,
    color: theme.palette.error.main
  },
  boldName: {
    color: theme.palette.typography.labels,
    fontWeight: 600
  },
  subLabel: {
    marginRight: 10,
    fontSize: theme.typography.body2.fontSize
  },
  buttonsWrapper: {
    display: 'flex'
  },
  tableCheckbox: {
    marginRight: 0
  },
  duplicateIcon: {
    marginRight: `8px`
  }
}));
